import { useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Typography } from 'antd';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ContentWrapper, EllipsisButton } from '@/components/_common';

import { useChartTheme, useConvertChartData, useDisclosure } from '@/hooks';
import FinancialClashFlowModal from './FinancialClashFlowModal';
import moment from 'moment';
import { useEffect } from 'react';
import { useChartCashFlow } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import { DateTimeFormat } from '@/utils/moment';
import {
  CashFlowDateType,
  CashFlowParams,
  CashFlowSituation,
  CashFlowType,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import { CashFlowChartFormValues } from './FinancialClashFlowModal/FinancialClashFlowForm';

const { Title, Paragraph } = Typography;

const defaultStartDate = moment().startOf('month').startOf('day');

function FinancialClashFlow() {
  const intl = useIntl();
  const chartTheme = useChartTheme();
  const [modalVisible, modalVisibleAction] = useDisclosure();

  const chartCashFLow = useChartCashFlow();

  const chartData = useConvertChartData(chartCashFLow.data?.chartData);

  useEffect(() => {
    chartCashFLow.mutateAsync({
      initialDate: defaultStartDate.format(DateTimeFormat),
      type: CashFlowType.period,
      situation: [CashFlowSituation.open, CashFlowSituation.settled],
      dateType: CashFlowDateType.dueDate,
    });
  }, []);

  const handleOk = async (values: CashFlowChartFormValues) => {
    try {
      const obj: CashFlowParams = {
        ...values,
        initialDate: values.initialDate.format(DateTimeFormat),
      };

      await chartCashFLow.mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  const handleFilterByDate = (filterByDate: CashFlowDateType) => {
    if (filterByDate === CashFlowDateType.dueDate) {
      return 'Vencimento';
    } else if (filterByDate === CashFlowDateType.payDate) {
      return 'Pagamento';
    } else {
      return 'Emissão';
    }
  };

  const initialDate = chartCashFLow.variables?.initialDate;
  const chartType = chartCashFLow.variables?.type;
  const dateType = chartCashFLow.variables?.dateType;

  const dates = `Período: ${moment(initialDate).format('DD/MM/YYYY')} a ${
    chartType === CashFlowType.weekly
      ? moment(initialDate).endOf('week').format('DD/MM/YYYY')
      : moment(initialDate).endOf('month').endOf('week').format('DD/MM/YYYY')
  } (A/L por ${dateType ? handleFilterByDate(dateType) : '-'})`;

  return (
    <>
      <ContentWrapper
        style={{ height: '430px', padding: '8px' }}
        extra={<EllipsisButton onClick={modalVisibleAction.open} />}
      >
        <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
          {`Fluxo de Caixa (${
            chartType === CashFlowType.period ? 'Periodo' : 'Semanal'
          })`}
        </Title>

        <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
          {dates}
        </Paragraph>

        <ResponsiveLine
          data={chartData ? chartData : []}
          enableSlices="x"
          margin={{ top: 15, right: 70, bottom: 90, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          yFormat={(value) => `${currencyFormatter(value)}`}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          colors={{ scheme: 'set2' }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: intl.formatMessage({
              id: 'dashboard.averageTicketReport.averageTicketPartsAndServices',
            }),
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh
          theme={chartTheme}
        />
      </ContentWrapper>

      <FinancialClashFlowModal
        open={modalVisible}
        handleOk={handleOk}
        onCancel={modalVisibleAction.close}
      />
    </>
  );
}

export default FinancialClashFlow;
